import React, { useState, useEffect } from 'react'
import FormDescription from 'components/FormDescription'
import FormLinks from 'components/FormLinks'
import withQueryParams from 'components/withQueryParams'
import yup from 'yup'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { Form, SecondaryLink, withNotifier } from 'stylist'
import { password, passwordConfirmation } from 'utils/validations'
import {
  isIntervuRedirect,
  sendIntervuAnalyticsEvent,
} from '../../utils/analytics'
import { authenticate } from 'redux-simple-auth'
import { getUriForEmailInput } from 'utils/sso'

const defaultError = 'An error has occurred'

const resetErrors = {
  reset_password_token: {
    'has expired, please request a new one':
      'Your password reset token has expired. Please request a new one',
    'is invalid':
      'Your password reset token is invalid. Please request a new one',
  },
  password: {
    'was already taken in the past!':
      'Please select a password you have not used in the previous 4 updates',
    'must be different to the current password!':
      'Please select a password that is different than your current password',
  },
}

const serializeErrorMessages = ({ errors }) => {
  const messages = Object.keys(errors).reduce(
    (messages, errorKey) => [
      ...messages,
      Object.keys(resetErrors[errorKey])
        .filter((key) => (errors[errorKey] || []).includes(key))
        .map((key) => resetErrors[errorKey][key]),
    ],
    []
  )

  return messages.length ? messages : defaultError
}

const redirectQueryParams = (query) => {
  const params = new URLSearchParams()

  params.set('email', query.get('email'))

  if (query.get('redirect_uri')) {
    params.set('redirect_uri', query.get('redirect_uri'))
  }

  return params
}

const schema = yup.object().shape({ password, passwordConfirmation })

const RouteLoginResetPassword = ({ authenticate, notify, query }) => {
  let mounted = true
  const [redirectUrl, setRedirectUrl] = useState('')

  const check2FASecret = (value) => {
    getUriForEmailInput(
      value,
      () => mounted && setRedirectUrl(`/login?${query.toString()}`),
      () =>
        mounted &&
        setRedirectUrl(
          query.get('hostname')
            ? `/login/redirecting?${query.toString()}`
            : query.get('returnTo')
            ? '/login/redirecting'
            : `/login?${redirectQueryParams(query)}`
        )
    )
  }

  useEffect(() => {
    if (query.get('email')) {
      check2FASecret(query.get('email'))
    }

    return () => {
      mounted = false
    }
  }, [])
  
  return (
    <Form
      initialValues={{ password: '', passwordConfirmation: '' }}
      redirect={redirectUrl}
      onSubmit={({ password, passwordConfirmation }) => {
        if (isIntervuRedirect(query)) {
          sendIntervuAnalyticsEvent('intervu-sign-up', 'password-chosen')
        }
        return fetch(`${process.env.REACT_APP_API_HOST}/users/password`, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          method: 'PATCH',
          body: JSON.stringify({
            user: {
              password,
              password_confirmation: passwordConfirmation,
              reset_password_token: query.get('reset_password_token'),
            },
          }),
        })
          .then(
            async (response) => {
              if (!response.ok) {
                return Promise.reject(
                  serializeErrorMessages(await response.json())
                )
              }

              notify('success', 'Password set successfully')

              return response.json()
            },
            () => Promise.reject('An error has occurred')
          )
          .then((response) => {
            if (!response.otp_required_for_login) {
              authenticate('oauth2-password-grant', {
                email: query.get('email'),
                password,
              })
            }
          })
      }}
      validationSchema={schema}
    >
      <Form.Title>Set new password</Form.Title>
      <FormDescription>
        Passwords must be a minimum of 12 characters long and contain a capital,
        lowercase, number, and special character.
      </FormDescription>
      <Form.Errors />
      <Form.Group>
        <label htmlFor="password">New Password</label>
        <Form.Input id="password" type="password" name="password" />
      </Form.Group>
      <Form.Group>
        <label htmlFor="passwordConfirmation">Confirm Password</label>
        <Form.Input
          id="passwordConfirmation"
          type="password"
          name="passwordConfirmation"
        />
      </Form.Group>
      <Form.Group buttons>
        <Form.SubmitButton block>Submit</Form.SubmitButton>
      </Form.Group>
      <Form.Group>
        <FormLinks>
          <SecondaryLink to="/login">Sign in</SecondaryLink>
          <SecondaryLink to="/login/forgot-password">
            Forgot password
          </SecondaryLink>
        </FormLinks>
      </Form.Group>
    </Form>
  )
}

export default compose(
  connect(null, { authenticate }),
  withQueryParams,
  withNotifier
)(RouteLoginResetPassword)



// WEBPACK FOOTER //
// ./src/routes/login/reset-password.js